<template>
  <div class="h-screen flex bg-zinc-950 text-zinc-100 overflow-hidden font-sans">
    
    <!-- Left Sidebar Panel -->
    <div class="w-full max-w-sm bg-zinc-900 border-r border-zinc-800 flex flex-col h-full shrink-0 z-10">
      
      <!-- App Header -->
      <div class="p-4 border-b border-zinc-800 flex justify-between items-center shrink-0">
        <div>
          <h1 class="text-xl font-bold text-sky-400 tracking-tight">Explorer</h1>
          <p class="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider">Carnet de voyage</p>
        </div>
        <button @click="handleLogout" class="text-zinc-500 hover:text-red-400 p-2 rounded-lg hover:bg-zinc-800 transition-colors" title="Se déconnecter">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
        </button>
      </div>

      <!-- Action Panel (Add / AI / Scrape) -->
      <div class="p-4 border-b border-zinc-800 space-y-3 shrink-0 bg-zinc-950/20">
        
        <!-- Standard creation button -->
        <button @click="openCreateStandard" class="w-full py-2.5 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
          Ajouter un lieu
        </button>

        <div class="grid grid-cols-2 gap-2">
          <button @click="activeActionSection = activeActionSection === 'ai' ? null : 'ai'" 
                  class="py-2 text-xs font-semibold rounded-lg border border-zinc-800 transition-colors flex items-center justify-center gap-1"
                  :class="activeActionSection === 'ai' ? 'bg-sky-500/10 border-sky-500/30 text-sky-400' : 'bg-zinc-900 text-zinc-400 hover:text-zinc-200'">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
            Assistant IA
          </button>
          <button @click="activeActionSection = activeActionSection === 'share' ? null : 'share'" 
                  class="py-2 text-xs font-semibold rounded-lg border border-zinc-800 transition-colors flex items-center justify-center gap-1"
                  :class="activeActionSection === 'share' ? 'bg-sky-500/10 border-sky-500/30 text-sky-400' : 'bg-zinc-900 text-zinc-400 hover:text-zinc-200'">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 10.742l-1.922-.641A3.001 3.001 0 1110 4c.66 0 1.277.213 1.782.576l1.922-.641A3.001 3.001 0 1121 14c-.66 0-1.277-.213-1.782-.576l-1.922.641A3.001 3.001 0 1110 20c-.66 0-1.277-.213-1.782-.576l-1.922.641z"></path></svg>
            Partager lien
          </button>
        </div>

        <!-- AI Assistant Collapsible Section -->
        <div v-if="activeActionSection === 'ai'" class="p-3 bg-zinc-950/60 rounded-xl border border-zinc-800 space-y-2 animate-in slide-in-from-top duration-200">
          <label class="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Décrivez votre lieu</label>
          <textarea v-model="aiPrompt" rows="2" placeholder="Ex: Rando cascade du saut du Gier dans le Pilat, magnifique vue..."
                    class="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-xs text-zinc-100 placeholder-zinc-700 outline-none focus:border-sky-500 resize-none"></textarea>
          <button @click="handleAiParse" :disabled="aiLoading" class="w-full py-1.5 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/20 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1">
            <span v-if="aiLoading" class="w-3 h-3 border border-sky-400 border-t-transparent rounded-full animate-spin"></span>
            {{ aiLoading ? 'Analyse...' : 'Générer la fiche' }}
          </button>
        </div>

        <!-- Web Share Scraper Collapsible Section -->
        <div v-if="activeActionSection === 'share'" class="p-3 bg-zinc-950/60 rounded-xl border border-zinc-800 space-y-2 animate-in slide-in-from-top duration-200">
          <label class="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Lien du lieu (Insta / Web)</label>
          <input type="url" v-model="shareUrl" placeholder="https://instagram.com/... ou https://..."
                 class="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-xs text-zinc-100 placeholder-zinc-700 outline-none focus:border-sky-500" />
          <button @click="handleScrapeUrl" :disabled="scrapeLoading" class="w-full py-1.5 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/20 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1">
            <span v-if="scrapeLoading" class="w-3 h-3 border border-sky-400 border-t-transparent rounded-full animate-spin"></span>
            {{ scrapeLoading ? 'Scraping...' : 'Pré-remplir la fiche' }}
          </button>
        </div>

      </div>

      <!-- Filters & Search -->
      <div class="p-4 border-b border-zinc-800 space-y-3 shrink-0">
        <div class="relative">
          <input type="text" v-model="searchQuery" placeholder="Rechercher un lieu..."
                 class="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-9 pr-4 py-2 text-xs text-zinc-100 placeholder-zinc-600 focus:border-sky-500 outline-none" />
          <svg class="w-4 h-4 text-zinc-600 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
        </div>

        <!-- Geographic Detailed Filters -->
        <div class="grid grid-cols-3 gap-1.5 pt-0.5">
          <select v-model="selectedCountry" class="bg-zinc-950 border border-zinc-800 text-[10px] text-zinc-400 p-1.5 rounded-lg focus:border-sky-500 outline-none">
            <option :value="null">Pays (Tous)</option>
            <option v-for="c in availableCountries" :key="c" :value="c">{{ c }}</option>
          </select>
          <select v-model="selectedRegion" class="bg-zinc-950 border border-zinc-800 text-[10px] text-zinc-400 p-1.5 rounded-lg focus:border-sky-500 outline-none" :disabled="availableRegions.length === 0">
            <option :value="null">Régions (Toutes)</option>
            <option v-for="r in availableRegions" :key="r" :value="r">{{ r }}</option>
          </select>
          <select v-model="selectedCity" class="bg-zinc-950 border border-zinc-800 text-[10px] text-zinc-400 p-1.5 rounded-lg focus:border-sky-500 outline-none" :disabled="availableCities.length === 0">
            <option :value="null">Villes (Toutes)</option>
            <option v-for="ci in availableCities" :key="ci" :value="ci">{{ ci }}</option>
          </select>
        </div>

        <!-- Tags filter list -->
        <div class="flex flex-wrap gap-1 max-h-16 overflow-y-auto pr-1">
          <button @click="selectedFilterTag = null" 
                  class="px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-colors"
                  :class="!selectedFilterTag ? 'bg-sky-500/10 border-sky-500/30 text-sky-400' : 'bg-zinc-800 border-zinc-700/50 text-zinc-400 hover:text-zinc-200'">
            Tous
          </button>
          <button v-for="tag in allTags" :key="tag" @click="toggleFilterTag(tag)"
                  class="px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-colors"
                  :class="selectedFilterTag === tag ? 'bg-sky-500/10 border-sky-500/30 text-sky-400' : 'bg-zinc-800 border-zinc-700/50 text-zinc-400 hover:text-zinc-200'">
            {{ tag }}
          </button>
        </div>
      </div>

      <!-- POI List (Scrollable) -->
      <div class="flex-1 overflow-y-auto p-4 space-y-3">
        <div v-if="filteredPois.length === 0" class="text-center py-8 text-zinc-500 text-xs italic">
          Aucun lieu enregistré ou ne correspond à vos filtres.
        </div>
        <div v-for="poi in filteredPois" :key="poi.id" 
             @click="focusPoi(poi)"
             class="p-3 bg-zinc-950/40 hover:bg-zinc-950/80 border border-zinc-800 hover:border-zinc-700 rounded-xl cursor-pointer transition-all space-y-2 relative group">
          
          <div class="pr-12">
            <h3 class="font-bold text-sm text-zinc-200 group-hover:text-sky-400 transition-colors truncate">{{ poi.name }}</h3>
            <p v-if="poi.address" class="text-[10px] text-zinc-500 truncate flex items-center gap-1">
              <svg class="w-3 h-3 text-sky-400/80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              {{ poi.address }}
            </p>
          </div>

          <p v-if="poi.description" class="text-xs text-zinc-400 line-clamp-2">{{ poi.description }}</p>

          <div v-if="poi.tags.length > 0" class="flex flex-wrap gap-1 pt-1">
            <span v-for="t in poi.tags" :key="t" class="bg-zinc-800 border border-zinc-700/50 text-zinc-400 text-[9px] font-semibold px-2 py-0.5 rounded-md">{{ t }}</span>
          </div>

          <!-- Edit / Delete actions -->
          <div class="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button @click.stop="openEditPoi(poi)" class="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 p-1.5 rounded-lg border border-zinc-700/30 transition-colors" title="Modifier">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
            </button>
            <button @click.stop="handleDeletePoi(poi.id)" class="bg-red-950/20 hover:bg-red-500 text-red-400 hover:text-white p-1.5 rounded-lg border border-red-500/20 transition-all" title="Supprimer">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
            </button>
          </div>

        </div>
      </div>

    </div>

    <!-- Right Map View Panel -->
    <div class="flex-1 h-full relative z-0">
      <div id="map" class="w-full h-full"></div>
      
      <!-- Leaflet Loading State overlay -->
      <div v-if="!leafletLoaded" class="absolute inset-0 bg-zinc-950 flex flex-col items-center justify-center z-50">
        <span class="w-10 h-10 border-4 border-sky-400 border-t-transparent rounded-full animate-spin mb-3"></span>
        <span class="text-zinc-400 text-sm">Chargement de la carte interactive...</span>
      </div>
    </div>

    <!-- POI Add/Edit Form Modal -->
    <PoiFormModal
      :show="showFormModal"
      :poi="editingPoi"
      :existing-tags="allTags"
      @close="showFormModal = false"
      @saved="handlePoiSaved"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import PoiFormModal from './PoiFormModal.vue'

const router = useRouter()
const pois = ref<any[]>([])
const searchQuery = ref('')
const selectedFilterTag = ref<string | null>(null)

const selectedCountry = ref<string | null>(null)
const selectedRegion = ref<string | null>(null)
const selectedCity = ref<string | null>(null)

watch(selectedCountry, () => {
  selectedRegion.value = null
  selectedCity.value = null
})
watch(selectedRegion, () => {
  selectedCity.value = null
})

// UI sections collapsible states
const activeActionSection = ref<'ai' | 'share' | null>(null)
const aiPrompt = ref('')
const aiLoading = ref(false)
const shareUrl = ref('')
const scrapeLoading = ref(false)

// Modals & Edit POI states
const showFormModal = ref(false)
const editingPoi = ref<any | null>(null)

// Leaflet specific states
const leafletLoaded = ref(false)
let map: any = null
let markersGroup: any = null
const markersMap = new Map<number, any>()

// List all tags in the POI list
const allTags = computed(() => {
  const tagsSet = new Set<string>()
  pois.value.forEach(p => {
    if (p.tags) {
      p.tags.forEach((t: string) => tagsSet.add(t))
    }
  })
  return Array.from(tagsSet)
})

// Filter POI list
const filteredPois = computed(() => {
  return pois.value.filter(p => {
    const matchesSearch = searchQuery.value.trim() === '' || 
      p.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
      (p.address && p.address.toLowerCase().includes(searchQuery.value.toLowerCase()))
    
    const matchesTag = !selectedFilterTag.value || p.tags.includes(selectedFilterTag.value)
    
    const matchesCountry = !selectedCountry.value || p.country === selectedCountry.value
    const matchesRegion = !selectedRegion.value || p.region === selectedRegion.value
    const matchesCity = !selectedCity.value || p.city === selectedCity.value
    
    return matchesSearch && matchesTag && matchesCountry && matchesRegion && matchesCity
  })
})

// Lists of unique values for address filters
const availableCountries = computed(() => {
  return Array.from(new Set(pois.value.map(p => p.country).filter(Boolean))).sort()
})

const availableRegions = computed(() => {
  let filtered = pois.value
  if (selectedCountry.value) {
    filtered = filtered.filter(p => p.country === selectedCountry.value)
  }
  return Array.from(new Set(filtered.map(p => p.region).filter(Boolean))).sort()
})

const availableCities = computed(() => {
  let filtered = pois.value
  if (selectedCountry.value) {
    filtered = filtered.filter(p => p.country === selectedCountry.value)
  }
  if (selectedRegion.value) {
    filtered = filtered.filter(p => p.region === selectedRegion.value)
  }
  return Array.from(new Set(filtered.map(p => p.city).filter(Boolean))).sort()
})

const toggleFilterTag = (tag: string) => {
  selectedFilterTag.value = selectedFilterTag.value === tag ? null : tag
}

const handleLogout = () => {
  localStorage.removeItem('apiToken')
  localStorage.removeItem('userRoles')
  router.push({ name: 'explorer_login' })
}

// Fetch POIs from backend
const fetchPois = async () => {
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/explorer/pois', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (response.ok) {
      pois.value = await response.json()
      updateMapMarkers()
    }
  } catch (e) {
    console.error(e)
  }
}

// AI parsing prompt handler
const handleAiParse = async () => {
  if (!aiPrompt.value.trim()) return
  aiLoading.value = true
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/explorer/ai/parse', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ prompt: aiPrompt.value })
    })

    if (response.ok) {
      const data = await response.json()
      // Prefill modal using parsed details
      editingPoi.value = null // Creating new POI
      showFormModal.value = true
      
      // Wait for watch inside PoiFormModal to trigger
      await nextTick()
      
      // We manually construct a prefilled object in local storage or inside the modal
      // We can also directly pass prefilled variables into a prefill object
      // For convenience, we temporarily override the editingPoi with the parsed properties!
      editingPoi.value = {
        name: data.name || '',
        description: data.description || '',
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        address: data.address || '',
        tags: data.tags || [],
        links: data.links || [],
        media: []
      }
      aiPrompt.value = ''
      activeActionSection.value = null
    }
  } catch (e) {
    console.error(e)
    alert('Une erreur est survenue lors de la communication avec l\'assistant IA.')
  } finally {
    aiLoading.value = false
  }
}

// URL scraper share handler
const handleScrapeUrl = async () => {
  if (!shareUrl.value.trim()) return
  scrapeLoading.value = true
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/explorer/share/scrape', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ url: shareUrl.value })
    })

    if (response.ok) {
      const data = await response.json()
      // Prefill form modal using scraped info
      editingPoi.value = null
      showFormModal.value = true
      await nextTick()
      
      editingPoi.value = {
        name: data.title || '',
        description: data.description || '',
        latitude: null,
        longitude: null,
        address: '',
        tags: [],
        links: [{ title: 'Lien Partagé', url: shareUrl.value }],
        media: []
      }
      shareUrl.value = ''
      activeActionSection.value = null
    }
  } catch (e) {
    console.error(e)
    alert('Une erreur est survenue lors de l\'analyse du lien.')
  } finally {
    scrapeLoading.value = false
  }
}

const openCreateStandard = () => {
  editingPoi.value = null
  showFormModal.value = true
}

const openEditPoi = (poi: any) => {
  editingPoi.value = poi
  showFormModal.value = true
}

const handleDeletePoi = async (poiId: number) => {
  if (!confirm('Voulez-vous vraiment supprimer ce point d\'intérêt ?')) return
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch(`/api/explorer/pois/${poiId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (response.ok) {
      pois.value = pois.value.filter(p => p.id !== poiId)
      updateMapMarkers()
    }
  } catch (e) {
    console.error(e)
  }
}

const handlePoiSaved = () => {
  fetchPois()
}

// Focus on map POI
const focusPoi = (poi: any) => {
  if (map && poi.latitude && poi.longitude) {
    map.setView([poi.latitude, poi.longitude], 13)
    const marker = markersMap.get(poi.id)
    if (marker) {
      marker.openPopup()
    }
  }
}

// Load Leaflet library dynamically
const loadLeaflet = () => {
  return new Promise<void>((resolve) => {
    if ((window as any).L) {
      resolve()
      return
    }
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(link)

    const script = document.createElement('script')
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    script.onload = () => resolve()
    document.head.appendChild(script)
  })
}

// Init Leaflet map
const initMap = () => {
  const L = (window as any).L
  if (!L) return

  map = L.map('map', {
    zoomControl: false
  }).setView([46.2276, 2.2137], 6) // Center in France by default

  L.control.zoom({
    position: 'bottomright'
  }).addTo(map)

  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; OpenStreetMap &copy; CARTO'
  }).addTo(map)

  markersGroup = L.featureGroup().addTo(map)

  // Listen to map clicks to grab coordinates for new POIs
  map.on('click', (e: any) => {
    if (confirm(`Voulez-vous poser un nouveau point d'intérêt ici ?\n(Lat: ${e.latlng.lat.toFixed(5)}, Lng: ${e.latlng.lng.toFixed(5)})`)) {
      editingPoi.value = null
      showFormModal.value = true
      nextTick().then(() => {
        editingPoi.value = {
          name: '',
          description: '',
          latitude: parseFloat(e.latlng.lat.toFixed(5)),
          longitude: parseFloat(e.latlng.lng.toFixed(5)),
          address: '',
          tags: [],
          links: [],
          media: []
        }
      })
    }
  })
}

// Sync DB POIs to Map Markers
const updateMapMarkers = () => {
  const L = (window as any).L
  if (!L || !markersGroup) return

  // Clear existing markers
  markersGroup.clearLayers()
  markersMap.clear()

  pois.value.forEach(poi => {
    if (poi.latitude && poi.longitude) {
      // Build PopUp HTML content
      let popupHtml = `<div class="p-2 text-zinc-950 font-sans max-w-xs">
        <h3 class="font-bold text-sm border-b pb-1 mb-1">${poi.name}</h3>`
      
      if (poi.address) {
        popupHtml += `<p class="text-[10px] text-zinc-500 mb-1"><b>Adresse :</b> ${poi.address}</p>`
      }
      if (poi.description) {
        popupHtml += `<p class="text-xs text-zinc-700 mb-2">${poi.description}</p>`
      }
      
      // Add links
      if (poi.links && poi.links.length > 0) {
        popupHtml += `<div class="mb-2">`
        poi.links.forEach((l: any) => {
          popupHtml += `<a href="${l.url}" target="_blank" class="text-sky-600 hover:underline block text-[10px] font-semibold">${l.title || 'Lien externe'}</a>`
        })
        popupHtml += `</div>`
      }

      // Add thumbnail previews
      if (poi.media && poi.media.length > 0) {
        popupHtml += `<div class="grid grid-cols-3 gap-1 mt-2">`
        poi.media.forEach((m: any) => {
          if (m.type === 'image') {
            popupHtml += `<div class="aspect-video bg-zinc-100 rounded overflow-hidden"><img src="${m.url}" class="object-cover w-full h-full" /></div>`
          } else {
            popupHtml += `<div class="aspect-video bg-zinc-200 rounded flex items-center justify-center text-zinc-600 font-bold text-[8px]">VIDEO</div>`
          }
        })
        popupHtml += `</div>`
      }

      popupHtml += `</div>`

      // Custom marker icon using simple style
      const customIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div class="w-6 h-6 bg-sky-500 border-2 border-zinc-900 rounded-full shadow-lg flex items-center justify-center text-zinc-950 hover:bg-sky-400 hover:scale-115 transition-all"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path></svg></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 24]
      })

      const marker = L.marker([poi.latitude, poi.longitude], { icon: customIcon })
        .bindPopup(popupHtml)
        .addTo(markersGroup)

      markersMap.set(poi.id, marker)
    }
  })

  // Fit bounds if we have markers
  if (pois.value.some(p => p.latitude && p.longitude)) {
    map.fitBounds(markersGroup.getBounds(), { padding: [40, 40] })
  }
}

// Watch tags and query filters to update map markers accordingly
watch([filteredPois], () => {
  const L = (window as any).L
  if (!L || !markersGroup) return

  // Temporarily update markers in bounds or hide excluded ones
  markersGroup.clearLayers()
  filteredPois.value.forEach(poi => {
    const marker = markersMap.get(poi.id)
    if (marker) {
      marker.addTo(markersGroup)
    }
  })
})

onMounted(async () => {
  await loadLeaflet()
  leafletLoaded.value = true
  await nextTick()
  initMap()
  await fetchPois()
})
</script>

<style>
/* Leaflet DivIcon reset styling to allow beautiful HTML styling */
.custom-div-icon {
  background: transparent !important;
  border: none !important;
}
.leaflet-popup-content-wrapper {
  background: #ffffff !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #e4e4e7 !important;
}
.leaflet-popup-tip {
  background: #ffffff !important;
}
</style>
